import { useEffect, useRef } from "react";
import { motion, useMotionValue, useTransform, useMotionTemplate, useReducedMotion, useScroll } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Eyebrow } from "./Reveal";
import { scrollToId, imgUrl } from "@/lib/api";

const SERVICES = [
  {
    title: "Entretien des espaces verts",
    img: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=900&q=80",
    desc: "Un jardin net et soigné toute l'année.",
    ditems: ["Tonte", "Taille de haies", "Débroussaillage", "Entretien régulier"],
    items: ["Tonte de pelouse", "Débroussaillage", "Taille de haies", "Taille d'arbustes", "Désherbage", "Nettoyage et remise en état de jardins"],
    testid: "service-card-espaces-verts",
  },
  {
    title: "Élagage & entretien extérieur",
    img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=900&q=80",
    desc: "Des arbres maîtrisés, un extérieur sécurisé.",
    ditems: ["Élagage", "Coupe de branches", "Entretien d'arbres", "Évacuation des déchets verts"],
    items: ["Élagage", "Coupe de branches", "Entretien d'arbres", "Évacuation des déchets verts", "Nettoyage après intervention"],
    testid: "service-card-elagage",
  },
  {
    title: "Nettoyage extérieur & intérieur",
    img: "https://images.unsplash.com/photo-1584467735815-f778f274e296?auto=format&fit=crop&w=900&q=80",
    desc: "Extérieur & intérieur comme neufs.",
    ditems: ["Terrasses", "Allées", "Murets", "Nettoyage intérieur"],
    items: ["Terrasses", "Allées", "Murets", "Cours", "Mobilier extérieur", "Remise au propre d'espaces extérieurs", "Nettoyage intérieur"],
    testid: "service-card-nettoyage",
  },
];

const CUSTOM = {
  title: "Services & petits travaux",
  desc: "Un besoin particulier ? Nous étudions les demandes spécifiques et les interventions sur mesure.",
  items: ["Petites réparations", "Missions ponctuelles", "Demandes spécifiques", "Interventions sur mesure"],
  testid: "service-card-sur-mesure",
};

const clamp01 = (v) => Math.max(0, Math.min(1, v));
// Desktop/tablet: monotonic sequential reveal — once open, stays open
const revealAt = (a, i) => clamp01((a - i * 0.85) / 0.6);

function CheckItem({ children }) {
  return (
    <li className="flex items-center gap-2.5 text-sm text-cream/75">
      <Check className="h-4 w-4 shrink-0 text-terra" strokeWidth={3} aria-hidden="true" />
      {children}
    </li>
  );
}

function CtaLink() {
  return (
    <button
      onClick={() => scrollToId("#contact")}
      data-testid="service-custom-cta"
      className="group inline-flex items-center gap-2 text-sm font-semibold text-terra"
    >
      <span className="relative">
        Parler de mon besoin
        <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-terra transition-transform duration-300 group-hover:scale-x-100" />
      </span>
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );
}

/* ---------- Desktop / tablet : compact cards, inner content reveal ---------- */

function DesktopCard({ s, i, af }) {
  const factor = useTransform(af, (a) => revealAt(a, i));
  const imgH = useTransform(factor, [0, 1], [230, 132]);
  const height = useMotionTemplate`${imgH}px`;
  const imgScale = useTransform(factor, [0, 1], [1, 1.06]);
  const contentOpacity = useTransform(factor, [0.35, 0.9], [0, 1]);
  const contentY = useTransform(factor, [0.35, 1], [12, 0]);
  const shadow = useTransform(factor, (f) => `0 24px 60px -24px rgba(0,0,0,${0.45 * f})`);

  return (
    <motion.article
      style={{ boxShadow: shadow }}
      data-testid={s.testid}
      className="relative flex flex-col rounded-2xl overflow-hidden border border-cream/10 bg-white/[0.04] h-[340px] xl:h-[380px]"
    >
      <motion.div style={{ scaleX: factor }} className="h-0.5 origin-left bg-terra shrink-0" aria-hidden="true" />
      <motion.div style={{ height }} className="relative shrink-0 overflow-hidden" data-testid={`service-img-${i}`}>
        <motion.img src={s.img} alt={s.title} loading="lazy" style={{ scale: imgScale }} className="h-full w-full object-cover" />
      </motion.div>
      <div className="p-6 flex flex-col flex-1 min-h-0">
        <div className="flex items-baseline gap-3 shrink-0">
          <span className="font-mono text-xs text-terra">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="font-display font-semibold text-base xl:text-lg text-cream leading-snug">{s.title}</h3>
        </div>
        <motion.div style={{ opacity: contentOpacity, y: contentY }} className="mt-2.5 min-h-0">
          <p className="text-sm text-cream/60 leading-relaxed">{s.desc}</p>
          <ul className="mt-2.5 space-y-1">
            {s.ditems.map((it) => (
              <CheckItem key={it}>{it}</CheckItem>
            ))}
          </ul>
        </motion.div>
      </div>
    </motion.article>
  );
}

function DesktopCustomCard({ s, i, af }) {
  const factor = useTransform(af, (a) => revealAt(a, i));
  const headerY = useTransform(factor, [0, 1], [96, 0]);
  const contentOpacity = useTransform(factor, [0.35, 0.9], [0, 1]);
  const contentY = useTransform(factor, [0.35, 1], [12, 0]);
  const ctaOpacity = useTransform(factor, [0.6, 1], [0, 1]);
  const ctaPe = useTransform(factor, (f) => (f > 0.6 ? "auto" : "none"));
  const watermarkOpacity = useTransform(factor, [0, 1], [0.12, 0.05]);
  const shadow = useTransform(factor, (f) => `0 24px 60px -24px rgba(0,0,0,${0.45 * f})`);

  return (
    <motion.article
      style={{ boxShadow: shadow }}
      data-testid={s.testid}
      className="relative flex flex-col rounded-2xl overflow-hidden border border-cream/10 bg-white/[0.04] h-[340px] xl:h-[380px]"
    >
      <motion.div style={{ scaleX: factor }} className="h-0.5 origin-left bg-terra shrink-0" aria-hidden="true" />
      <motion.span
        aria-hidden="true"
        style={{ opacity: watermarkOpacity }}
        className="pointer-events-none select-none absolute -right-3 -top-8 font-display font-bold text-[160px] leading-none text-terra"
      >
        {String(i + 1).padStart(2, "0")}
      </motion.span>
      <div className="p-6 flex flex-col flex-1 min-h-0">
        <motion.div style={{ y: headerY }} className="flex items-baseline gap-3 shrink-0">
          <span className="font-mono text-xs text-terra">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="font-display font-semibold text-base xl:text-lg text-cream leading-snug">{s.title}</h3>
        </motion.div>
        <motion.div style={{ opacity: contentOpacity, y: contentY }} className="mt-2.5 min-h-0">
          <p className="text-sm text-cream/60 leading-relaxed">{s.desc}</p>
          <ul className="mt-2.5 space-y-1">
            {s.ditems.map((it) => (
              <CheckItem key={it}>{it}</CheckItem>
            ))}
          </ul>
        </motion.div>
        <motion.div style={{ opacity: ctaOpacity, pointerEvents: ctaPe }} className="mt-auto pt-4 shrink-0">
          <CtaLink />
        </motion.div>
      </div>
    </motion.article>
  );
}

function ServicesCta({ af, startAt = 3.3 }) {
  const factor = useTransform(af, (a) => clamp01((a - startAt) / 0.6));
  const opacity = useTransform(factor, [0, 1], [0, 1]);
  const y = useTransform(factor, [0, 1], [18, 0]);
  const pe = useTransform(factor, (f) => (f > 0.5 ? "auto" : "none"));

  return (
    <motion.div style={{ opacity, y, pointerEvents: pe }} className="mt-8 sm:mt-10 flex justify-center">
      <button
        onClick={() => scrollToId("#contact")}
        data-testid="services-cta"
        className="group relative cta-playful inline-flex items-center gap-3 rounded-full bg-terra px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#b05f41]"
      >
        Demander un devis gratuit
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </motion.div>
  );
}

/* ---------- Mobile : sticky heading, compact cards grow with scroll ---------- */
/* Each card opens as it climbs the screen, stays open, closes only on the way back up */

function MobileCard({ s, i, reduce }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.82", "start 0.42"] });
  const forcedOpen = useMotionValue(1);
  const factor = reduce ? forcedOpen : scrollYProgress;
  const imgH = useTransform(factor, [0, 1], [312, 110]);
  const height = useMotionTemplate`${imgH}px`;
  const imgScale = useTransform(factor, [0, 1], [1, 1.05]);
  const contentOpacity = useTransform(factor, [0.35, 0.9], [0, 1]);
  const contentY = useTransform(factor, [0.35, 1], [10, 0]);

  return (
    <motion.article
      ref={ref}
      data-testid={s.testid}
      className="rounded-2xl overflow-hidden bg-white/[0.04] border border-cream/10 flex flex-col h-[380px]"
    >
      <motion.div style={{ height }} className="overflow-hidden shrink-0" data-testid={`service-img-${i}`}>
        <motion.img src={s.img} alt={s.title} loading="lazy" style={{ scale: imgScale }} className="h-full w-full object-cover" />
      </motion.div>
      <div className="p-5 flex flex-col flex-1 min-h-0">
        <div className="flex items-baseline gap-3 shrink-0">
          <span className="font-mono text-xs text-terra">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="font-display font-semibold text-lg text-cream leading-snug">{s.title}</h3>
        </div>
        <motion.ul style={{ opacity: contentOpacity, y: contentY }} className="mt-3 space-y-2">
          {s.items.map((it) => (
            <li key={it} className="flex items-start gap-2.5 text-sm text-cream/70">
              <Check className="h-4 w-4 mt-0.5 shrink-0 text-terra" strokeWidth={3} aria-hidden="true" />
              {it}
            </li>
          ))}
        </motion.ul>
      </div>
    </motion.article>
  );
}

function MobileCustomCard({ s, i, reduce }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.82", "start 0.42"] });
  const forcedOpen = useMotionValue(1);
  const factor = reduce ? forcedOpen : scrollYProgress;
  const headerY = useTransform(factor, [0, 1], [120, 0]);
  const contentOpacity = useTransform(factor, [0.35, 0.9], [0, 1]);
  const contentY = useTransform(factor, [0.35, 1], [10, 0]);
  const watermarkOpacity = useTransform(factor, [0, 1], [0.12, 0.05]);

  return (
    <motion.article
      ref={ref}
      data-testid={s.testid}
      className="relative rounded-2xl overflow-hidden bg-white/[0.04] border border-cream/10 flex flex-col h-[380px]"
    >
      <motion.span
        aria-hidden="true"
        style={{ opacity: watermarkOpacity }}
        className="pointer-events-none select-none absolute -right-2 -top-6 font-display font-bold text-[130px] leading-none text-terra"
      >
        {String(i + 1).padStart(2, "0")}
      </motion.span>
      <div className="p-5 flex flex-col flex-1 min-h-0">
        <motion.div style={{ y: headerY }} className="flex items-baseline gap-3 shrink-0">
          <span className="font-mono text-xs text-terra">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="font-display font-semibold text-lg text-cream leading-snug">{s.title}</h3>
        </motion.div>
        <motion.div style={{ opacity: contentOpacity, y: contentY }} className="mt-3 min-h-0">
          <p className="text-sm text-cream/60 leading-relaxed">{s.desc}</p>
          <ul className="pt-3 space-y-2">
            {s.items.map((it) => (
              <li key={it} className="flex items-start gap-2.5 text-sm text-cream/70">
                <Check className="h-4 w-4 mt-0.5 shrink-0 text-terra" strokeWidth={3} aria-hidden="true" />
                {it}
              </li>
            ))}
          </ul>
          <div className="pt-4">
            <CtaLink />
          </div>
        </motion.div>
      </div>
    </motion.article>
  );
}

/* ---------- Section ---------- */

export default function Services({ settings = {}, services: servicesProp = [] }) {
  const reduce = useReducedMotion();
  const wrapRef = useRef(null);
  const progress = useMotionValue(0);
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches;

  useEffect(() => {
    if (reduce) return;
    const onScroll = () => {
      const el = wrapRef.current;
      if (!el) return;
      const total = el.offsetHeight - window.innerHeight;
      if (total <= 0) return;
      progress.set(Math.min(1, Math.max(0, -el.getBoundingClientRect().top / total)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [reduce, progress]);

  const FALLBACK = [
    ...SERVICES.map((s) => ({ ...s, kind: "service" })),
    { kind: "cta", title: CUSTOM.title, desc: CUSTOM.desc, img: "", items: CUSTOM.items, ditems: CUSTOM.items },
  ];
  const raw = Array.isArray(servicesProp) && servicesProp.length ? servicesProp : FALLBACK;
  const list = raw.map((s, i) => ({
    kind: s.kind === "cta" ? "cta" : "service",
    title: s.title,
    desc: s.desc ?? s.description ?? "",
    img: s.img ? imgUrl(s.img) : "",
    items: s.items || [],
    ditems: s.ditems || (s.items || []).slice(0, 4),
    testid: `service-card-${i + 1}`,
  }));
  const n = list.length;

  const afDesktop = useTransform(progress, (p) => p * (n * 0.85 + 1.2) - 0.55);
  const afStatic = useMotionValue(10);
  const activeDesktop = reduce ? afStatic : afDesktop;

  return (
    <section id="services" data-testid="services-section" className="bg-[#10180f] text-cream overflow-x-clip">
      {isMobile ? (
        <div className="relative">
          <div className="sticky top-16 z-20 bg-[#10180f] px-4 pt-8 pb-5">
            <Eyebrow light>Nos services</Eyebrow>
            <h2 className="mt-3 font-display font-semibold tracking-tight leading-snug text-2xl text-cream" data-testid="services-title">
              Un seul contact pour vos besoins d'entretien
            </h2>
            <p className="mt-3 text-cream/70 text-sm leading-relaxed">
              Des interventions ponctuelles ou régulières pour particuliers, entreprises, commerces, copropriétés et collectivités.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 px-4 pt-2 pb-28" data-testid="services-cascade">
            {list.map((s, i) =>
              s.kind === "cta" ? (
                <MobileCustomCard key={s.title} s={s} i={i} reduce={reduce} />
              ) : (
                <MobileCard key={s.title} s={s} i={i} reduce={reduce} />
              )
            )}
          </div>
        </div>
      ) : (
        <div ref={wrapRef} className="relative" style={{ height: reduce ? "auto" : "200vh" }}>
          <div className={reduce ? "" : "sticky top-0 min-h-screen flex items-center"}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-[50px]">
              <Eyebrow light>Nos services</Eyebrow>
              <h2 className="mt-3 font-display font-semibold tracking-tight leading-snug text-2xl sm:text-3xl lg:text-4xl text-cream max-w-2xl" data-testid="services-title">
                Un seul contact pour vos besoins d'entretien
              </h2>
              <p className="mt-3 text-cream/70 text-sm sm:text-lg max-w-2xl leading-relaxed">
                Des interventions ponctuelles ou régulières pour particuliers, entreprises, commerces, copropriétés et collectivités.
              </p>

              <div className="mt-8 grid gap-3 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4" data-testid="services-cascade">
                {list.map((s, i) =>
                  s.kind === "cta" ? (
                    <DesktopCustomCard key={s.title} s={s} i={i} af={activeDesktop} />
                  ) : (
                    <DesktopCard key={s.title} s={s} i={i} af={activeDesktop} />
                  )
                )}
              </div>

              <ServicesCta af={activeDesktop} startAt={(n - 1) * 0.85 + 0.75} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
